import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApiError } from 'src/api/axiosClient/types';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { buildFormData, buildRequestData } from '../helpers';
import { useKeywordService } from '../services';
import { Keyword, KeywordFormData } from '../types';

export interface CreateKeywordFormDialogController {
  isOpen: boolean;
  initFormData: KeywordFormData | null;
  error: ApiError | null;
  open: (keyword?: Keyword) => void;
  close: () => void;
  submit: (
    full: KeywordFormData,
    dirty: Partial<KeywordFormData>,
    nextAction?: () => unknown | Promise<unknown>
  ) => Promise<void>;
}

export const useCreateKeywordFormDialogController = (): CreateKeywordFormDialogController => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;
  const [initFormData, setInitFormData] = useState<KeywordFormData | null>(null);

  const { createKeyword, updateKeyword, error } = useKeywordService();

  const open = useCallback((keyword?: Keyword) => {
    setInitFormData(buildFormData(keyword));
  }, []);

  const close = useCallback(() => {
    setInitFormData(null);
  }, []);

  const submit = useCallback(
    async (full: KeywordFormData, dirty: Partial<KeywordFormData>, nextAction?: () => Promise<unknown>) => {
      const { id: keywordId } = full;

      try {
        if (keywordId) {
          await updateKeyword(currentCompanyId, keywordId, buildRequestData(full));
        } else {
          await createKeyword(currentCompanyId, buildRequestData(full));
        }

        close();
        await nextAction?.();
      } catch (e) {
        console.log(e);
      }
    },
    []
  );

  return {
    isOpen: !!initFormData,
    initFormData,
    error,
    open,
    close,
    submit,
  };
};
